import React, { ChangeEvent, KeyboardEvent, SyntheticEvent, Component, ReactNode } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Field, FieldProps, FormikProps } from 'formik';
import { FormControl, Input as MuiInput, FormHelperText } from '@material-ui/core';

import { ContentApproval, GroupSourceType, ClientGroupSource } from 'api';
import { UrlSchema, UsernameSchema } from '../../validations';
import Settings from './Settings';

type FieldOnChange = FieldProps['field']['onChange'];

interface IFormValue {
  [name: string]: string;
}

interface IProps {
  statusAdornment: ReactNode;
  settingsAdornment: ReactNode;
  formikProps: FormikProps<ClientGroupSource>;
  onSave: () => void;
}

interface IPropsEnhanced extends IProps, WithTranslation {}

class Input extends Component<IPropsEnhanced> {
  onKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      this.onSubmit(event);
    }
  };

  onChange = (onChange: FieldOnChange) => (event: ChangeEvent<HTMLInputElement>) => {
    const { formikProps } = this.props;
    const { setFieldValue, values } = formikProps;

    const sourceValue = event.target.value;
    const isUrl = UrlSchema.isValidSync(sourceValue);
    const isUsername = UsernameSchema.isValidSync(sourceValue);
    let { source_type } = values;

    if (isUrl) {
      source_type = GroupSourceType.Rss;
    } else if (isUsername) {
      source_type = GroupSourceType.TwitterHandle;
    } else {
      source_type = GroupSourceType.Keyword;
    }

    setFieldValue('source_type', source_type);
    onChange(event);
  };

  onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();

    const { formikProps, onSave } = this.props;
    const { isValid } = formikProps;

    if (isValid) {
      onSave();
    }
  };

  getApprovalKey() {
    const { formikProps } = this.props;
    const { values } = formikProps;
    const { approval } = values;

    return approval === ContentApproval.Autoapproved ? 'common:on' : 'common:off';
  }

  render() {
    const { statusAdornment, settingsAdornment, formikProps, t } = this.props;
    const { values } = formikProps;

    return (
      <Field
        name="source_value"
        render={(fieldProps: FieldProps<IFormValue>) => {
          const { errors, touched } = fieldProps.form;
          const { value, name, onChange } = fieldProps.field;

          const fieldValue = value || '';
          const isSaved = !!fieldProps.form.values.id;
          const hasError = !!(errors[name] && touched[name]);
          const nameError = touched[name] && errors[name];

          return (
            <FormControl fullWidth margin="normal" error={hasError}>
              <MuiInput
                onKeyPress={this.onKeyPress}
                onBlur={this.onSubmit}
                disabled={!!values.id}
                disableUnderline
                value={fieldValue}
                placeholder={t('groups:source')}
                onChange={this.onChange(onChange)}
                inputProps={{ name }}
                endAdornment={statusAdornment}
              />
              {isSaved && (
                <Settings approvalKey={this.getApprovalKey()} adornment={settingsAdornment} />
              )}
              {nameError && <FormHelperText>{nameError}</FormHelperText>}
            </FormControl>
          );
        }}
      />
    );
  }
}

export default withTranslation()(Input);
